'use client';

import './payment-method-card.scss';

import { format, isValid, parse } from 'date-fns';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Badge, BadgeProps } from '@sola/ark-core/atoms/badge/badge.js';
import {
  ActionCard,
  ActionCardBody,
  ActionCardDivider,
  ActionCardHeader,
  ActionCardSection,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.js';
import {
  GraphQLCardExpiryStatus,
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { slugify } from '@sola/utils/string.js';

import {
  PaymentMethodIcon,
  getLabel,
} from '../../atoms/paymentmethod-icon/paymentmethod-icon';
import { CommitmentAvatarGroup } from '../commitment-avatar-group/commitment-avatar-group';
import { PaymentMethodCardFooter } from './payment-method-card-footer';

/**
 * Get payment method details.
 */
const getDetails = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) => {
  if (paymentMethod.creditCard) {
    const expiryDate = paymentMethod.creditCard.expirationDate
      ? parse(paymentMethod.creditCard.expirationDate, 'MM/yyyy', new Date())
      : null;
    if (!isValid(expiryDate)) {
      return;
    }
    if (paymentMethod.creditCard.expiryStatus === 'EXPIRED') {
      return t('payments.methodDetails.expiredCard', {
        expirationDate: format(expiryDate!, 'M/yy'),
        interpolation: { escapeValue: false },
      });
    }
    return t('payments.methodDetails.card', {
      expirationDate: format(expiryDate!, 'M/yy'),
      interpolation: { escapeValue: false },
    });
  }
  if (paymentMethod.ach) {
    return t('payments.methodDetails.ach', {
      lastFour: paymentMethod.ach.lastFourOfAccountNumber,
    });
  }
};

export type PaymentMethodCardProps = {
  commitments?: GraphQLCommitment[];
  paymentMethod: GraphQLSupporterPaymentMethod;
};

/**
 * Get payment card data.
 */
export const usePaymentMethodCardData = ({
  paymentMethod,
  commitments,
}: PaymentMethodCardProps) => {
  const { t } = useTranslation('my');
  return {
    t,
    label: getLabel(paymentMethod, t),
    details: getDetails(paymentMethod, t),
    recurringCommitments: commitments?.length
      ? commitments
          .filter(
            (commitment) =>
              commitment.isRecurring &&
              commitment.status === CommitmentStatusEnum.Active,
          )
          .sort((a, b) => {
            const sponsorship = CommitmentTypeEnum.ChildSponsorship;
            if (
              a.commitmentType === sponsorship &&
              b.commitmentType !== sponsorship
            ) {
              return -1;
            }
            if (
              a.commitmentType !== sponsorship &&
              b.commitmentType === sponsorship
            ) {
              return 1;
            }
            return 0;
          })
      : [],
    // TODO will prob need to handle other cases.
    isWarning:
      paymentMethod.creditCard?.expiryStatus ===
      GraphQLCardExpiryStatus.ExpiringSoon,
    isError:
      paymentMethod.creditCard?.expiryStatus ===
      GraphQLCardExpiryStatus.Expired,
  };
};

const getDefaultHeading = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) =>
  paymentMethod.creditCard
    ? t('my:updatePaymentMethod.accountTypes.creditCard')
    : t('my:updatePaymentMethod.accountTypes.bankAccount');

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = (props) => {
  const { details, isError, isWarning, label, recurringCommitments, t } =
    usePaymentMethodCardData(props);
  const { paymentMethod } = props;
  const { accountNickname } = paymentMethod;
  const hasBadge = paymentMethod.creditCard?.expiryStatus;

  return (
    <ActionCard
      className={cn(`PaymentMethodCard `, {
        'PaymentMethodCard--warning': isWarning,
        'PaymentMethodCard--error': isError,
        [`PaymentMethodCard--${slugify(paymentMethod.paymentType!)}`]:
          !!paymentMethod.paymentType,
      })}
      // Easy code > debatable rule...
      // eslint-disable-next-line no-nested-ternary
      color={isWarning ? 'warning' : isError ? 'negative' : undefined}
    >
      <ActionCardSection>
        <ActionCardHeader
          className={cn([
            // TODO: this is a temporary solution to make room for badge, but we need a scalable solution because it will keep reocurring.
            hasBadge
              ? 'mb-comfy-xs md:mb-comfy-sm'
              : 'mb-comfy-sm md:mb-comfy-md',
          ])}
        >
          {/* TODO our label utilities have some issues */}
          <h3 className="label-potent md:label-potent label-md md:label-lg">
            {accountNickname || getDefaultHeading(paymentMethod, t)}
          </h3>

          {paymentMethod.creditCard?.expiryStatus && (
            <Badge
              {...({
                [GraphQLCardExpiryStatus.ExpiringSoon]: {
                  color: 'warning',
                  iconBefore: 'alert-triangle',
                  label: t('cardExpiring'),
                },
                [GraphQLCardExpiryStatus.Expired]: {
                  color: 'negative',
                  iconBefore: 'alert-circle',
                  label: t('cardExpired'),
                },
              }[paymentMethod.creditCard.expiryStatus] as BadgeProps)}
            />
          )}
        </ActionCardHeader>
        <ActionCardBody>
          <PaymentMethodIcon paymentMethod={paymentMethod} />
          <div className="">
            <span className="block label-md label-moderate md:label-lg mb-snug-md">
              {label}
            </span>
            {details && (
              <span
                className={cn(
                  'block label-sm label-moderate label-neutral-mild md:label-md',
                  {
                    'label-warning-moderate': isWarning,
                    'label-negative-moderate': isError,
                  },
                )}
              >
                {details}
              </span>
            )}
          </div>
          {!!recurringCommitments?.length && (
            <CommitmentAvatarGroup
              commitments={recurringCommitments}
              itemsToShow={3}
              tooltipMessage={t('payments.methodCommitmentsTooltip', {
                count: recurringCommitments.length,
              })}
            />
          )}
        </ActionCardBody>
      </ActionCardSection>
      <ActionCardDivider />
      <PaymentMethodCardFooter
        recurringCommitments={recurringCommitments}
        paymentMethod={paymentMethod}
      />
    </ActionCard>
  );
};
